import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Info } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) => `€ ${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function StatCard({ label, value, sub, accent = "blue", warn }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[accent] || colors.blue}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wider opacity-70 font-medium">
        <span>{label}</span>
        {warn && <Badge className="bg-yellow-100 text-yellow-800 text-[9px] font-normal px-1.5 py-0">Stima</Badge>}
      </div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

export default function AdminHealthPage() {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: r }] = await Promise.all([
        axios.get(`${API}/admin/health-detailed`, { withCredentials: true }),
        axios.get(`${API}/admin/revenue-summary`, { withCredentials: true }),
      ]);
      setData(h); setRevenue(r);
    } catch { toast.error("Errore caricamento health"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data || !revenue) return <div className="text-center py-12 text-[#0D1F3C]/40" data-testid="admin-health-page">Caricamento…</div>;

  const topPlan = revenue.breakdown_per_piano?.[0];
  const stripeWarn = !revenue.stripe_available;

  return (
    <div className="space-y-6" data-testid="admin-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0D1F3C]">Console Admin · Health</h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1">Aggiornato: {new Date(data.checked_at).toLocaleString("it-IT")}</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-health-refresh">
          <ArrowsClockwise size={16} className="mr-2" />Aggiorna
        </Button>
      </div>

      {/* REVENUE — sopra tutto */}
      <div data-testid="admin-revenue-section">
        <h3 className="text-sm font-semibold text-[#0D1F3C]/70 uppercase tracking-wider mb-3 flex items-center gap-2">
          Revenue
          {stripeWarn && (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded">
              <Info size={12} />Stima da listino (Stripe non collegato)
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="MRR" value={fmtEur(revenue.mrr)} accent="green" warn={stripeWarn} />
          <StatCard label="ARR" value={fmtEur(revenue.arr)} accent="green" warn={stripeWarn} />
          <StatCard label="Incassato Mese" value={fmtEur(revenue.incasso_mese_corrente)}
            sub={revenue.stripe_available ? "Da Stripe" : "Da DB locale"} accent="blue" />
          <StatCard label="Atteso Mese" value={fmtEur(revenue.incasso_mese_atteso)} accent="purple" warn={stripeWarn} />
          <StatCard label="Top Piano" value={topPlan?.plan_name || "—"}
            sub={topPlan ? `${topPlan.count} clienti · ${fmtEur(topPlan.mrr)}/mese` : "Nessun pagante"}
            accent="orange" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-[#0D1F3C]/70 uppercase tracking-wider mb-3">Utenti</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Totale" value={data.users.total} accent="blue" />
          <StatCard label="Trial" value={data.users.trial} accent="orange" />
          <StatCard label="Paganti" value={data.users.paid} accent="green" />
          <StatCard label="Sospesi" value={data.users.suspended} accent="red" />
          <StatCard label="Personal" value={data.users.personal} accent="blue" />
          <StatCard label="Business" value={data.users.business} accent="purple" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-[#0D1F3C]/70 uppercase tracking-wider mb-3">Attività</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Workspaces attivi" value={data.workspaces.total} accent="purple" />
          <StatCard label="Candidati 24h" value={data.candidates.last_24h}
            sub={`7d: ${data.candidates.last_7d} · 30d: ${data.candidates.last_30d}`} accent="blue" />
          <StatCard label="Analisi completate 24h" value={data.tests_completed.last_24h}
            sub={`7d: ${data.tests_completed.last_7d} · 30d: ${data.tests_completed.last_30d}`} accent="green" />
          <StatCard label="Simulazioni 30d" value={data.simulations_last_30d}
            sub={`Reports 30d: ${data.reports_last_30d}`} accent="orange" />
        </div>
      </div>

      {/* SUPPORTO */}
      <div data-testid="admin-support-section">
        <h3 className="text-sm font-semibold text-[#0D1F3C]/70 uppercase tracking-wider mb-3">Supporto</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Ticket aperti" value={data.tickets?.open || 0} accent="orange" />
          <StatCard label="In lavorazione" value={data.tickets?.in_progress || 0} accent="blue" />
          <StatCard label="Risolti 24h" value={data.tickets?.resolved_24h || 0} accent="green" />
          <StatCard label="Tempo medio risposta" value={`${data.tickets?.avg_response_hours || 0}h`}
            accent={data.tickets?.avg_response_hours > 12 ? "red" : "green"} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-[#0D1F3C]/70 uppercase tracking-wider mb-3">Infrastruttura</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Mongo latency" value={`${data.mongo_latency_ms}ms`}
            sub={data.mongo_latency_ms < 50 ? "OK" : data.mongo_latency_ms < 200 ? "Slow" : "Critical"}
            accent={data.mongo_latency_ms < 50 ? "green" : data.mongo_latency_ms < 200 ? "orange" : "red"} />
        </div>
      </div>
    </div>
  );
}
